import React from 'react';

import type {NoExtension, Section, SectionItem, SelectAllMechanism} from 'common/components/items-menu/types';
import {PropertyType, ResourceType, SCD_PROFILE_PROPERTY_PREFIX} from 'common/report/constants';
import type {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import type {SpecialTimeUnit} from 'common/types/reports/bookmark';
import type {Cohort} from 'common/util/definitions-types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {Behavior, CustomProperty, Segmentation} from 'common/types/reports/query';
import {Directions} from 'common/components/drop-menu/types';
import {Translatable} from 'common/i18n';
import type {ReportState} from 'common/report/insights/contexts/report-bookmark-manager/types';

import {Event} from '../events-menu/types';
import {InlineCohortSegment} from '../segment-menu/types';
import type {GroupLabelParams} from '../count-menu/constants';
import {EventOrPropertySectionItem, EventSectionItem, PropertySectionItem} from './item-menu-types';

/**
 * The "value" of a property is usually a string (representing the property name)
 * but can also be an array of strings in the case of a lookup table property.
 * When it's a lookup table, the last value is the lookup property name and the
 * preceding values are the path (usually just the join key)
 * TODO: This eventually be something more strict like the following:
 * type RawPropertyValue = string;
 * type LookupPropertyValue = [string, string];
 * type PropertyValue = RawPropertyValue | LookupPropertyValue[];
 */
export type PropertyValue = string | string[];

/**
 * Defines all the different property types that this dropdown can render
 *
 * aims to replace the convoluted way of defining what this menu renders by
 * using attributes like `project-has-people`, `project-has-cohorts`, etc
 */
export enum MenuItemType {
  BehaviorWithEvent = `behavior_with_event`,
  BehaviorWithProperty = `behavior_with_property`,
  BehaviorMultiAttribution = `behavior_multi_attribution`,
  BehaviorMultiAttributionFilter = `behavior_multi_attribution_filter`,
  AggregateUserByGroup = `aggregate_user_by_group`,
  // "Borrow Property..." menu item
  BorrowProperty = `borrow_property`,
  SCDProperty = `scd_property`,
  Events = `events`,
  EventWithProperties = `events_with_properties`,
  EventCustomProperties = `event_custom_properties`,
  UserCustomProperties = `user_custom_properties`,
  LookupTableProperties = `lookup_table_properties`,
  EventProperties = `event_properties`,
  UserProperties = `user_properties`,
  RuntimeProperty = `create_custom_runtime_property`,
  Cohorts = `cohorts`,
  DataGroupProperties = `datagroup_properties`,
  Other = `other`,
  ComputedListPropertySum = `list_property_sum`,
  ComputedListPropertyUniques = `list_property_uniques`,
  ComputedListLength = `list_length`,
}

export type Property = {
  /** represents a computed property based on each user's event stream */
  behavior?: Behavior;
  /** used for borrowed properties, where this field indicates the event that the property is borrowed from */
  borrowEvent?: Event;
  customProperty?: CustomProperty;
  customPropertyId?: string | number;
  dataGroupId?: string;
  displayOptions?: `BehaviorMetricDisplay`;
  dynamicLabel?: (params: GroupLabelParams) => Translatable;
  /**
   * used for experiment property modifiers
   */
  experiments?: Segmentation[`experiments`];
  joinPropertyType?: PropertyType;
  label?: Translatable; // this seems to only be used for special (like $all_people) or "other" properties (like $paths)
  listItemGroup?: Segmentation;
  menuItemType?: MenuItemType;
  /**
   * True for an object property's inner keys, which are bare leaf names with no
   * Lexicon definitions of their own — a same-named top-level property's
   * metadata must not apply to them.
   */
  isObjectInnerKey?: boolean;
  propertyDefaultType: PropertyType | null;
  propertyObjectKey?: string | string[];
  resourceType: ResourceType;
  /**
   * Whether or not this is a Slowly Changing Dimension property
   */
  scdEvent?: string;
  /**
   * used for shifting line to the right for scd props
   */
  scdOffset?: Behavior[`offset`];
  /**
   * Used to specify as of time for revenue change type in view users
   */
  scdOptions?: Segmentation[`scdOptions`];
  tempDataGroupId?: string;
  type: PropertyType | null;
  /** Used to specify a unit within a date property. For example, when segmenting on a date */
  unit?: TimeUnit | SpecialTimeUnit;
  value: PropertyValue;
};

export type InlineCohort = {
  resourceType: ResourceType.Cohort;
  value: InlineCohortSegment;
  label: string;
};

export type SavedCohort = {
  resourceType: ResourceType.Cohort;
  value: number;
  label: string;
  dataGroupId?: string;
};

export type DataGroupItem = DataGroup & {
  properties: any[];
  isLoading: boolean;
};

/**
 * These represents the serializable form of a PropertyItem.
 * This list should not contain any UI properties as is saved to the bookmark
 * Any properties should also be included in SerializedItemKeyMap
 */
export type Item = Property | SavedCohort | InlineCohort;

export function isCohort(item: Item): item is InlineCohort | SavedCohort {
  return item?.resourceType === ResourceType.Cohort;
}

export function isSavedCohort(item: Item): item is SavedCohort {
  return isCohort(item) && typeof item.value === `number`;
}

export function isInlineCohort(item: Item): item is InlineCohort {
  return isCohort(item) && typeof item.value !== `number` && !!item.value.cohort;
}

export function getCohortDataGroupId(item: SavedCohort | InlineCohort): string | null {
  const dataGroupId = isInlineCohort(item) ? item.value.cohort.raw_cohort.data_group_id : item.dataGroupId;
  return dataGroupId || null;
}

export function isProperty(item: Item): item is Property {
  return item && item.resourceType !== ResourceType.Cohort;
}

export function isScdProperty(item: Item): item is Property {
  return (
    isProperty(item) &&
    !!item.value &&
    typeof item.value === `string` &&
    item.value.startsWith(SCD_PROFILE_PROPERTY_PREFIX)
  );
}

export function isEventSectionItem(item: EventSectionItem | PropertySectionItem): item is EventSectionItem {
  return item && item.menuItemType === MenuItemType.Events;
}

export function isPropertySectionItem(item: EventSectionItem | PropertySectionItem): item is PropertySectionItem {
  return item && item.menuItemType !== MenuItemType.Events;
}

export type Selection = Item | Item[];

type ItemIntersection = Property & SavedCohort & InlineCohort;

/**
 * This boolean map is used to filter any property item to only the keys in Item
 */
export const SerializedItemKeyMap: {
  [P in keyof Required<ItemIntersection>]: boolean;
} = {
  customPropertyId: true,
  behavior: true,
  customProperty: true,
  dataGroupId: true,
  joinPropertyType: true,
  displayOptions: true,
  listItemGroup: true,
  isObjectInnerKey: false,
  propertyDefaultType: true,
  propertyObjectKey: true,
  resourceType: true,
  tempDataGroupId: true,
  type: true,
  unit: true,
  value: true,
  label: true,
  dynamicLabel: true,
  scdEvent: true,
  borrowEvent: true,
  menuItemType: false,
  scdOffset: true,
  scdOptions: true,
  experiments: true,
};

export const NonDataGroupPropertyItemIdentifierKeys: Array<keyof ItemIntersection> = [`customPropertyId`, `value`];

// Checked in order
export const PropertyItemPersistedIdentifierKeys: Array<keyof ItemIntersection> = [
  `tempDataGroupId`,
  `dataGroupId`,
  ...NonDataGroupPropertyItemIdentifierKeys,
];

export enum SpecialActions {
  CreateInlineCohort = `create_inline_cohort`,
  CreateCustomEventFormula = `create_custom_event_formula`,
  CreateCustomPeopleFormula = `create_custom_people_formula`,

  CreateCustomBehavioralProperties = `create_custom_behavioral_property`,
  NavigateBack = `navigate_back`,
  Refresh = `refresh`,
  CreateNew = `create_new`,
}

export type SpecialControl = {
  icon: string;
  isControlItem: boolean;
  propertyDefaultType: PropertyType | null;
  type: PropertyType | null;
  label: string;
  resourceType: ResourceType;
  value: SpecialActions;
  tooltipData?: {
    header: string;
    description: string;
    entityIcon: string;
    helpLink?: {
      label: string;
      href: string;
    };
  };
};

export enum PropertyItemAction {
  Typecast = `typecast`,
}

export enum PropertyCombineMode {
  Union = `union`,
  Intersection = `intersection`,
}

export type CohortItem = Cohort & {
  label: string;
  canView?: boolean;
  canUpdateBasic?: boolean;
  canShare?: boolean;
  isSuperadmin?: boolean;
};

export type GetSortedMenuSections = <T = Section>(sections: T[]) => T[];

/**
 * The type of the current screen of the properties menu.
 *
 * Note that this differs from MenuItemType, which is the type of each item in the menu. For example, if the user
 * selected an event property that's an object, then the menu will show items-menu of the fields within the object.
 * For this scenario, it has StagedItemType.Object and each field menu item will have MenuItemType.EventProperties.
 */
export enum StagedItemType {
  Typecast,
  Dimension,
  Datetime,
  PropertiesForEvent,
  Object,
  User,
  Group,
  Cohort,
  BehaviorWithEvent,
  BehaviorMultiAttribution,
  ComputedListProperty,
  ComputedListNumericProperty,
  BorrowEvent,
  Experiment,
}

export const AllTab = `all`;
export const EventsInPropertiesMenuTab = `events`;

export enum IncludeCustomPropertyType {
  All = `all`,
  None = `none`,
  PersistedOnly = `persisted-only`,
  PersistedNonBehavioralOnly = `persisted-non-behavioral-only`,
}

export enum TypecastPillAction {
  /**
   * @deprecated Typecasting should be selected through the overflow menu. This value is reserved for uses that do not currently have an overflow menu.
   */
  Typecast = `typecast`,
  TriggerTutorial = `trigger-tutorial`,
}

export interface PropertiesMenuParams {
  listProperty?: Segmentation;
  isModalOpen?: boolean;
  onModalOpen?: () => void;
  onModalClose?: () => void;
  closeMenu?: () => void;
  getSortedMenuSections?: GetSortedMenuSections;
  selected?: any;
  context?: string;

  cohortMultiselect?: boolean;
  cohortOperationType?: string;
  applyButtonText?: string;
  minSelection?: number;
  maxSelection?: number;
  allowBehavioralPropertyCreation?: boolean;
  allowTypecastPillAction?: TypecastPillAction;
  enableWorkspaceFilterRestrictions?: boolean;
  disabledItems?: Obj;
  includeCustomProperties?: IncludeCustomPropertyType;
  excludeLookupTableProperties?: boolean;
  showBorrowedProperties?: boolean;
  excludeDimensionProperties?: boolean;
  propertyCombineMode?: PropertyCombineMode;
  specialCohorts?: Obj[];
  showItemsCaret?: boolean;
  otherOptions?: Array<PropertySectionItem<string>>;
  selectAllMechanism?: SelectAllMechanism;
  showSelectAll?: boolean;
  selectAllDisabled?: boolean;
  hideRecentlyViewed?: boolean;
  projectIsFreePlan?: boolean;
  selectableSensitiveProperties?: boolean;
  descriptionEditDisabled?: boolean;
  menuItemTypes?: MenuItemType[];
  typeFilter?: PropertyType[];

  enableAllDataGroupsForScdProps?: boolean;
  disableAllScdProps?: boolean;

  /** If true, load all data groups from the store and triggers their respective properties request */
  allDataGroups?: boolean;
  /** Set of data group IDs from relevant metrics */
  dataGroupIds?: Set<string | null>;
  metricLevelDataGroups?: boolean;
  eventNames?: string[];
  hideAllPropertiesSelect?: boolean;
  hideEditControls?: boolean;
  hideEvents?: boolean;
  hideExperiments?: boolean;
  inlineCohortsOverride?: {enable: boolean};
  multiple?: boolean;
  open?: boolean;
  projectHasCohorts?: boolean;
  projectHasPeople?: boolean;
  projectId?: number;
  readonly?: boolean;
  showAllEvents?: boolean;
  showAnyEvent?: boolean;
  showSessionEvent?: boolean;
  showSpecialDatasetProperty?: boolean;
  showSpecialDatePropertyUnit?: boolean;
  showTopEvents?: boolean;
  supportB2bCohorts?: boolean;
  trackName?: string;
  userId?: number;
  menuHeader?: React.ReactNode;
  // TODO(fcm) remove everywhere once first class metrics is GA
  contextPanelAlignment?: Directions;
  // this is used in events page to avoid removing required columns
  excludeClearProperties?: string[];
  customWidth?: number;

  // todo: audit this, parents shouldn't care about this
  onStagedProperty?: (stagedItems: EventOrPropertySectionItem[]) => void;
  onDelete?: () => void;
  onSelect?: (item: SelectedPropertyItem) => void;
  onChangeTab?: (tab: string) => void;
  onClickBackButton?: () => void;
  onResetToDefault?: () => void;

  // propagated events
  onTooltipContextEditDescription?: (ev: CustomEvent) => void;
  onTrack?: (ev: CustomEvent) => void;

  // Below attributes read by ChildMenu methods
  workspaceId?: number;

  onRender?: () => void;

  reportBookmark?: ReportState;
}

export type SelectedPropertyItem<T = Selection> = {
  item: T;
  rawItem: PropertySectionItem;
  autoTriggered: boolean;
  commit?: boolean;
  menuItemType?: MenuItemType;
  stagedItemsStack?: EventOrPropertySectionItem[];
  onItemClicked?: boolean;
};

export enum SortBy {
  Relevance = `relevance`,
  NameAsc = `name-asc`,
  NameDesc = `name-desc`,
}

export enum FilterResourceType {
  All = `all`,
  Events = `events`,
  Computed = `computed`,
  Experiments = `experiments`,
}

export type FilterValue = {
  value: string;
  resourceType?: FilterResourceType | ResourceType.Cohort | ResourceType.Event | ResourceType.User;
  dataGroupId?: string;
};

export type FilterSectionItem = SectionItem<FilterValue, NoExtension>;

export enum ApplyButtonDisabledReason {
  None = `none`,
  TooManySelected = `too-many-selected`,
  TooFewSelected = `too-few-selected`,
}
